import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  MapPin, Ruler, Clock, CaretDown, Wrench, Sparkle, Cpu, Plus, X,
  Toolbox, UsersThree, ShieldCheck, ClockCounterClockwise, Microphone, ListChecks, FilePdf, DownloadSimple
} from '@phosphor-icons/react';
import { toast } from '../../hooks/use-toast';
import axiosInstance from '../../api/api';
import { projectTemplates, defaultLaborRates } from './constants';
import VoiceDictateButton from '../VoiceDictateButton';

const MAX_PHOTOS = 5;
const ALLOWED_EXTS = ['jpg', 'jpeg', 'png', 'webp'];
const PHOTO_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/ai-pricing/photo/`;

export const EstimateForm = ({ formData, onChange, onSubmit, loading, jobTypes, onApplyTemplate, photos, setPhotos, scopeItems = [], setScopeItems }) => {
  const [showTemplates, setShowTemplates] = useState(false);
  const [calibration, setCalibration] = useState(null);
  const calibrationTimerRef = useRef(null);

  useEffect(() => {
    const loc = (formData.location || '').trim();
    if (loc.length < 2) { setCalibration(null); return; }
    if (calibrationTimerRef.current) clearTimeout(calibrationTimerRef.current);
    calibrationTimerRef.current = setTimeout(async () => {
      try {
        const resp = await axiosInstance.get('/ai-pricing/cost-index', { params: { location: loc } });
        if (resp.data?.success) setCalibration(resp.data.calibration);
      } catch (e) { setCalibration(null); }
    }, 600);
    return () => { if (calibrationTimerRef.current) clearTimeout(calibrationTimerRef.current); };
  }, [formData.location]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [customLaborRate, setCustomLaborRate] = useState(null);
  const [contingencyPercent, setContingencyPercent] = useState(10);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const photoInputRef = useRef(null);
  const blueprintInputRef = useRef(null);
  const [analyzingBlueprint, setAnalyzingBlueprint] = useState(false);
  const [blueprint, setBlueprint] = useState(null);

  const handleBlueprintSelected = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;
    if (selected.length > 5) {
      toast({ title: 'Too many files', description: 'Upload up to 5 PDFs per analysis.', variant: 'destructive' });
      return;
    }
    for (const f of selected) {
      if (!f.name.toLowerCase().endsWith('.pdf')) {
        toast({ title: 'PDF only', description: `${f.name} — upload 2D CAD drawings as PDF files.`, variant: 'destructive' });
        return;
      }
      if (f.size > 15 * 1024 * 1024) {
        toast({ title: 'File too large', description: `${f.name} — 15 MB max per PDF.`, variant: 'destructive' });
        return;
      }
    }
    setAnalyzingBlueprint(true);
    try {
      const fd = new FormData();
      selected.forEach(f => fd.append('files', f));
      const resp = await axiosInstance.post('/ai-pricing/blueprint-analyze', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      const a = resp.data?.analysis;
      if (!a) throw new Error('empty');
      setBlueprint(a);
      // Auto-fill the form
      const descParts = [a.summary, a.notes ? `Blueprint notes: ${a.notes}` : null].filter(Boolean);
      const takeoffLines = (a.takeoff || []).slice(0, 40).map(t =>
        `- ${t.room && t.room !== 'ALL' ? `${t.room}: ` : ''}${t.item} — ${t.quantity} ${t.unit}${t.notes ? ` (${t.notes})` : ''}`);
      if (takeoffLines.length) descParts.push(`Material takeoff:\n${takeoffLines.join('\n')}`);
      const fileLabel = selected.map(f => f.name).join(', ');
      const newDesc = `${formData.description ? formData.description + '\n\n' : ''}[From blueprint${selected.length > 1 ? 's' : ''} "${fileLabel}"]\n${descParts.join('\n\n')}`;
      onChange({ target: { name: 'description', value: newDesc, type: 'text' } });
      if (a.total_area_sqft) onChange({ target: { name: 'square_footage', value: String(a.total_area_sqft), type: 'number' } });
      if (a.project_type && jobTypes.some(jt => jt.id === a.project_type)) {
        onChange({ target: { name: 'job_type', value: a.project_type, type: 'text' } });
      }
      if (Array.isArray(a.scope_items) && a.scope_items.length) {
        setScopeItems(prev => [...prev, ...a.scope_items.filter(s => !prev.includes(s))].slice(0, 50));
      }
      toast({ title: 'Blueprints read!', description: `Extracted ${a.scope_items?.length || 0} scope items, ${a.takeoff?.length || 0} takeoff lines${a.total_area_sqft ? `, ${a.total_area_sqft} sq ft` : ''}.` });
    } catch (err) {
      toast({ title: 'Blueprint analysis failed', description: err.response?.data?.detail || 'Could not read these PDFs. Try again or enter details manually.', variant: 'destructive' });
    }
    setAnalyzingBlueprint(false);
    if (blueprintInputRef.current) blueprintInputRef.current.value = '';
  };

  const laborRateInfo = defaultLaborRates[formData.job_type || 'general_contractor'] || defaultLaborRates.general_contractor;

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await axiosInstance.get('/ai-pricing/history');
      if (response.data.success) {
        setHistory(response.data.estimates);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const applyTemplate = (template) => {
    onApplyTemplate(template);
    setShowTemplates(false);
    toast({
      title: "Template Applied",
      description: `"${template.name}" has been loaded. Add your location to get an estimate.`,
    });
  };

  const handlePhotosSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (photos.length + files.length > MAX_PHOTOS) {
      toast({
        title: `Maximum ${MAX_PHOTOS} photos`,
        description: `You can upload up to ${MAX_PHOTOS} photos per estimate. You currently have ${photos.length}.`,
        variant: 'destructive',
      });
      if (photoInputRef.current) photoInputRef.current.value = '';
      return;
    }

    setUploadingPhotos(true);
    const uploaded = [];
    for (const f of files) {
      const ext = (f.name.split('.').pop() || '').toLowerCase();
      if (!ALLOWED_EXTS.includes(ext)) {
        toast({ title: 'Unsupported format', description: `${f.name} — only JPG, PNG, or WebP allowed.`, variant: 'destructive' });
        continue;
      }
      if (f.size > 8 * 1024 * 1024) {
        toast({ title: 'File too large', description: `${f.name} — 8 MB max per photo.`, variant: 'destructive' });
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('file', f);
        const resp = await axiosInstance.post('/ai-pricing/upload-photo', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (resp.data?.success) {
          uploaded.push({
            file_id: resp.data.file_id,
            url: `${PHOTO_BASE}${resp.data.file_id}`,
            name: f.name,
            size: resp.data.size,
          });
        }
      } catch (err) {
        toast({ title: 'Upload failed', description: err.response?.data?.detail || f.name, variant: 'destructive' });
      }
    }
    if (uploaded.length > 0) {
      setPhotos(prev => [...prev, ...uploaded]);
    }
    setUploadingPhotos(false);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const removePhoto = (file_id) => {
    setPhotos(prev => prev.filter(p => p.file_id !== file_id));
  };

  return (
    <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white shadow-[4px_4px_0px_0px_#000000] dark:shadow-[4px_4px_0px_0px_#2B4C6F]">
      <div className="p-4 border-b-2 border-black dark:border-white bg-neutral-50 dark:bg-neutral-900">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wrench size={20} weight="bold" className="text-[#2B4C6F]" />
            <h2 className="font-black text-neutral-900 dark:text-white uppercase tracking-wide text-sm">
              JOB DETAILS
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setShowTemplates(!showTemplates)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-2 border-black dark:border-white transition-all ${
              showTemplates 
                ? 'bg-[#2B4C6F] text-white' 
                : 'bg-white dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700'
            }`}
            data-testid="templates-toggle"
          >
            <Sparkle size={14} weight="fill" />
            Templates
          </button>
        </div>
      </div>
      
      {showTemplates && (
        <div className="border-b-2 border-black dark:border-white bg-amber-50 dark:bg-amber-900/20 p-4" data-testid="templates-panel">
          <div className="flex items-center gap-2 mb-3">
            <Sparkle size={16} weight="fill" className="text-amber-600" />
            <span className="font-bold text-neutral-900 dark:text-white text-sm uppercase tracking-wider">Quick-Fill Templates</span>
          </div>
          <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
            {projectTemplates.map((template) => {
              const IconComponent = template.icon;
              return (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => applyTemplate(template)}
                  className="flex items-start gap-2 p-3 bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-600 text-left hover:bg-amber-100 dark:hover:bg-amber-900/30 hover:border-amber-500 transition-all group"
                  data-testid={`template-${template.id}`}
                >
                  <IconComponent size={18} weight="bold" className="text-[#2B4C6F] mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-bold text-neutral-900 dark:text-white text-xs truncate group-hover:text-amber-700 dark:group-hover:text-amber-400">
                      {template.name}
                    </p>
                    <p className="text-[10px] text-neutral-500 truncate">
                      {template.square_footage} sq ft • {template.timeline}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      
      <form onSubmit={onSubmit} className="p-5 space-y-5">
        {/* Job Type */}
        <div>
          <Label className="font-bold text-neutral-900 dark:text-white uppercase tracking-wider text-xs mb-2 block">
            JOB TYPE *
          </Label>
          <div className="relative">
            <select
              name="job_type"
              value={formData.job_type}
              onChange={onChange}
              className="w-full h-12 px-4 pr-10 bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white font-medium text-neutral-900 dark:text-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#2B4C6F] focus:border-transparent"
              data-testid="job-type-select"
            >
              <option value="">Select type...</option>
              {jobTypes.map(jt => (
                <option key={jt.id} value={jt.id}>{jt.name}</option>
              ))}
            </select>
            <CaretDown size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none" />
          </div>
        </div>

        {/* Location */}
        <div>
          <Label className="font-bold text-neutral-900 dark:text-white uppercase tracking-wider text-xs mb-2 block">
            <MapPin size={14} weight="bold" className="inline mr-1" />
            LOCATION
          </Label>
          <Input
            name="location"
            value={formData.location}
            onChange={onChange}
            placeholder="City, State"
            className="h-12 border-2 border-black dark:border-white bg-white dark:bg-[#0F172A] rounded-none focus:ring-2 focus:ring-[#2B4C6F]"
            data-testid="location-input"
          />
          {calibration && calibration.match !== 'national' && (
            <div
              className="mt-2 flex items-center gap-2 text-xs font-mono bg-[#2B4C6F]/10 dark:bg-[#2B4C6F]/30 border border-[#2B4C6F]/30 px-2 py-1.5"
              data-testid="calibration-preview"
            >
              <MapPin size={13} weight="fill" className="text-[#2B4C6F] dark:text-[#7BA3CC] shrink-0" />
              <span className="text-neutral-700 dark:text-neutral-300">
                <span className="font-bold capitalize">{calibration.area}</span>
                {' · cost index '}<span className="font-bold">{calibration.index}</span>
                {' ('}
                <span className={calibration.pct_vs_national > 0 ? 'text-red-600 dark:text-red-400 font-bold' : calibration.pct_vs_national < 0 ? 'text-green-700 dark:text-green-400 font-bold' : 'font-bold'}>
                  {calibration.pct_vs_national > 0 ? '+' : ''}{calibration.pct_vs_national}% vs national
                </span>
                {')'}
              </span>
            </div>
          )}
        </div>

        {/* Description */}
        <div>
          <Label className="font-bold text-neutral-900 dark:text-white uppercase tracking-wider text-xs mb-2 block">
            DESCRIPTION *
          </Label>
          <Textarea
            name="description"
            value={formData.description}
            onChange={onChange}
            placeholder="Describe the work needed..."
            className="min-h-[100px] border-2 border-black dark:border-white bg-white dark:bg-[#0F172A] rounded-none resize-none focus:ring-2 focus:ring-[#2B4C6F]"
            data-testid="description-input"
          />
          <p className="text-xs text-neutral-500 mt-1 font-mono">
            {formData.description.length}/20 MIN
          </p>
        </div>

        {/* Voice Scope Checklist */}
        <div className="border-2 border-dashed border-[#2B4C6F] bg-[#2B4C6F]/5 dark:bg-[#2B4C6F]/10 p-4" data-testid="voice-checklist-section">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div>
              <Label className="font-bold text-neutral-900 dark:text-white uppercase tracking-wider text-xs flex items-center gap-1 mb-1">
                <Microphone size={14} weight="bold" className="text-[#2B4C6F]" />
                DICTATE SCOPE CHECKLIST
              </Label>
              <p className="text-xs text-neutral-500">
                Talk through the job — AI turns your words into checklist items priced into the estimate. English/Spanish.
              </p>
            </div>
            <VoiceDictateButton
              mode="tasks"
              compact
              className="shrink-0"
              onResult={({ tasks, transcript }) => {
                const items = tasks?.length ? tasks : (transcript ? [transcript] : []);
                if (items.length === 0) return;
                setScopeItems((prev) => [...prev, ...items.filter((t) => !prev.includes(t))].slice(0, 50));
                toast({ title: 'Checklist updated', description: `Added ${items.length} item${items.length > 1 ? 's' : ''} from your dictation.` });
              }}
            />
          </div>

          {scopeItems.length > 0 && (
            <div className="mt-3" data-testid="voice-checklist-items">
              <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                {scopeItems.map((item, i) => (
                  <li key={`${item}-${i}`}
                    className="flex items-start justify-between gap-2 bg-white dark:bg-[#0F172A] border-2 border-black dark:border-neutral-600 px-3 py-1.5"
                    data-testid={`scope-item-${i}`}>
                    <span className="flex items-start gap-2 text-sm text-neutral-800 dark:text-neutral-200">
                      <ListChecks size={16} weight="bold" className="text-[#2B4C6F] mt-0.5 shrink-0" />
                      {item}
                    </span>
                    <button type="button" aria-label="Remove item"
                      onClick={() => setScopeItems((prev) => prev.filter((_, idx) => idx !== i))}
                      className="text-neutral-400 hover:text-red-600 shrink-0 mt-0.5"
                      data-testid={`scope-item-remove-${i}`}>
                      <X size={14} weight="bold" />
                    </button>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#2B4C6F]">{scopeItems.length} item{scopeItems.length > 1 ? 's' : ''} in scope</span>
                <button type="button" onClick={() => setScopeItems([])}
                  className="text-[11px] uppercase tracking-wider font-bold text-neutral-400 hover:text-red-600"
                  data-testid="scope-clear-all">Clear all</button>
              </div>
            </div>
          )}
        </div>

        {/* Square Footage & Timeline */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="font-bold text-neutral-900 dark:text-white uppercase tracking-wider text-xs mb-2 block">
              <Ruler size={14} weight="bold" className="inline mr-1" />
              SQ FT
            </Label>
            <Input
              name="square_footage"
              type="number"
              value={formData.square_footage}
              onChange={onChange}
              placeholder="1500"
              className="h-10 border-2 border-black dark:border-white bg-white dark:bg-[#0F172A] rounded-none text-sm"
              data-testid="sqft-input"
            />
          </div>
          <div>
            <Label className="font-bold text-neutral-900 dark:text-white uppercase tracking-wider text-xs mb-2 block">
              <Clock size={14} weight="bold" className="inline mr-1" />
              TIMELINE
            </Label>
            <Input
              name="timeline"
              value={formData.timeline}
              onChange={onChange}
              placeholder="2-3 weeks"
              className="h-10 border-2 border-black dark:border-white bg-white dark:bg-[#0F172A] rounded-none text-sm"
              data-testid="timeline-input"
            />
          </div>
        </div>

        {/* Materials Toggle */}
        <label className="flex items-center gap-3 p-3 bg-neutral-50 dark:bg-neutral-900 border-2 border-black dark:border-white cursor-pointer">
          <input
            type="checkbox"
            name="materials_included"
            checked={formData.materials_included}
            onChange={onChange}
            className="w-5 h-5 border-2 border-black dark:border-white bg-white dark:bg-[#0F172A] rounded-none accent-[#2B4C6F]"
            data-testid="materials-checkbox"
          />
          <div>
            <span className="font-bold text-neutral-900 dark:text-white text-sm">
              Include Materials
            </span>
            <p className="text-xs text-neutral-500">Uncheck for labor-only</p>
          </div>
        </label>

        {/* Blueprint PDF Upload — AI CAD Reading */}
        <div className="border-2 border-dashed border-amber-500 bg-amber-50/60 dark:bg-amber-900/10 p-4" data-testid="blueprint-upload-section">
          <div className="flex items-start justify-between mb-3">
            <div>
              <Label className="font-bold text-neutral-900 dark:text-white uppercase tracking-wider text-xs flex items-center gap-1 mb-1">
                <FilePdf size={14} weight="bold" className="text-amber-600" />
                UPLOAD BLUEPRINT — 2D CAD PDF
              </Label>
              <p className="text-xs text-neutral-500">
                Got plans? Upload up to 5 PDFs (floor plans + elevations) — AI reads dimensions, rooms, scope &amp; builds a material takeoff.
              </p>
            </div>
          </div>

          <input
            ref={blueprintInputRef}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            className="hidden"
            onChange={handleBlueprintSelected}
            data-testid="blueprint-upload-input"
          />

          <button
            type="button"
            onClick={() => blueprintInputRef.current?.click()}
            disabled={analyzingBlueprint}
            className="w-full h-12 bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white font-black uppercase tracking-wider text-xs text-amber-700 dark:text-amber-400 hover:bg-amber-500 hover:text-black transition-colors disabled:opacity-60 disabled:cursor-wait"
            data-testid="blueprint-upload-button"
          >
            {analyzingBlueprint ? (
              <span className="flex items-center justify-center gap-2">
                <Cpu size={16} weight="bold" className="animate-spin" />
                READING BLUEPRINT... (CAN TAKE A MINUTE)
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Plus size={16} weight="bold" />
                {blueprint ? 'READ MORE PDFS' : 'CHOOSE BLUEPRINT PDFS'}
              </span>
            )}
          </button>

          {blueprint && !analyzingBlueprint && (
            <div className="mt-3 bg-white dark:bg-[#0F172A] border-2 border-black dark:border-neutral-600 p-3" data-testid="blueprint-analysis-card">
              <div className="flex items-start justify-between gap-2">
                <p className="font-bold text-sm text-neutral-900 dark:text-white truncate">
                  <FilePdf size={14} weight="bold" className="inline mr-1 text-amber-600" />
                  {blueprint.file_name}
                </p>
                <button type="button" onClick={() => setBlueprint(null)} aria-label="Dismiss blueprint summary"
                  className="text-neutral-400 hover:text-red-600 shrink-0" data-testid="blueprint-dismiss">
                  <X size={14} weight="bold" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2 text-[11px] font-bold uppercase">
                {blueprint.files_analyzed > 1 && <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 border border-black">{blueprint.files_analyzed} files</span>}
                {blueprint.total_area_sqft && <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 border border-black">{blueprint.total_area_sqft} sq ft</span>}
                {blueprint.rooms?.length > 0 && <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 border border-black">{blueprint.rooms.length} rooms</span>}
                {blueprint.scope_items?.length > 0 && <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 border border-black">{blueprint.scope_items.length} scope items</span>}
                {blueprint.takeoff?.length > 0 && <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 border border-black">{blueprint.takeoff.length} takeoff lines</span>}
              </div>
              {blueprint.rooms?.length > 0 && (
                <ul className="mt-2 text-xs text-neutral-600 dark:text-neutral-300 space-y-0.5 max-h-28 overflow-y-auto">
                  {blueprint.rooms.map((r, i) => (
                    <li key={i}>• {r.name}{r.dimensions ? ` — ${r.dimensions}` : ''}{r.area_sqft ? ` (${r.area_sqft} sf)` : ''}</li>
                  ))}
                </ul>
              )}
              {blueprint.takeoff?.length > 0 && (
                <div className="mt-3" data-testid="blueprint-takeoff-table">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[11px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Material Takeoff</p>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const resp = await axiosInstance.post('/ai-pricing/takeoff/pdf', {
                            takeoff: blueprint.takeoff,
                            job_type: formData.job_type || blueprint.job_type || '',
                            location: formData.location || '',
                            total_area_sqft: blueprint.total_area_sqft || null,
                            files_analyzed: blueprint.files_analyzed || null,
                            notes: blueprint.notes || '',
                          }, { responseType: 'blob' });
                          const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `takeoff_${(formData.job_type || 'project')}.pdf`;
                          a.click();
                          URL.revokeObjectURL(url);
                          toast({ title: 'Takeoff PDF downloaded', description: 'Hand it to your supplier for quotes.' });
                        } catch (e) {
                          toast({ title: 'PDF export failed', variant: 'destructive' });
                        }
                      }}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white border border-black"
                      data-testid="takeoff-pdf-btn"
                    >
                      <DownloadSimple size={12} weight="bold" /> PDF
                    </button>
                  </div>
                  <div className="max-h-44 overflow-y-auto border-2 border-black dark:border-neutral-600">
                    <table className="w-full text-xs">
                      <thead className="bg-neutral-100 dark:bg-neutral-800 sticky top-0">
                        <tr className="text-left">
                          <th className="px-2 py-1 font-black uppercase text-[10px]">Room</th>
                          <th className="px-2 py-1 font-black uppercase text-[10px]">Material</th>
                          <th className="px-2 py-1 font-black uppercase text-[10px] text-right">Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {blueprint.takeoff.map((t, i) => (
                          <tr key={i} className="border-t border-neutral-200 dark:border-neutral-700" data-testid={`takeoff-row-${i}`}>
                            <td className="px-2 py-1 text-neutral-500 whitespace-nowrap">{t.room || '—'}</td>
                            <td className="px-2 py-1 text-neutral-800 dark:text-neutral-200">{t.item}{t.notes ? <span className="text-neutral-400"> ({t.notes})</span> : null}</td>
                            <td className="px-2 py-1 text-right font-mono font-bold whitespace-nowrap">{t.quantity} {t.unit}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {blueprint.notes && <p className="mt-2 text-[11px] text-neutral-500 italic">{blueprint.notes}</p>}
            </div>
          )}
        </div>

        {/* Photo Upload — AI Vision Estimating */}
        <div className="border-2 border-dashed border-[#2B4C6F] bg-[#2B4C6F]/5 dark:bg-[#2B4C6F]/10 p-4" data-testid="photo-upload-section">
          <div className="flex items-start justify-between mb-3">
            <div>
              <Label className="font-bold text-neutral-900 dark:text-white uppercase tracking-wider text-xs flex items-center gap-1 mb-1">
                <Sparkle size={14} weight="fill" className="text-[#2B4C6F]" />
                ADD PHOTOS — AI VISION
              </Label>
              <p className="text-xs text-neutral-500">
                Upload up to {MAX_PHOTOS} site photos. GPT-5.2 Vision will inspect them for a sharper estimate.
              </p>
            </div>
            <span className="font-mono text-xs text-neutral-500 whitespace-nowrap">
              {photos.length}/{MAX_PHOTOS}
            </span>
          </div>

          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
            multiple
            className="hidden"
            onChange={handlePhotosSelected}
            data-testid="photo-upload-input"
          />

          {photos.length < MAX_PHOTOS && (
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              disabled={uploadingPhotos}
              className="w-full h-12 bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white font-black uppercase tracking-wider text-xs text-[#2B4C6F] hover:bg-[#2B4C6F] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-wait"
              data-testid="photo-upload-button"
            >
              {uploadingPhotos ? (
                <span className="flex items-center justify-center gap-2">
                  <Cpu size={16} weight="bold" className="animate-spin" />
                  UPLOADING...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Plus size={16} weight="bold" />
                  {photos.length === 0 ? 'CHOOSE PHOTOS' : 'ADD MORE'}
                </span>
              )}
            </button>
          )}

          {photos.length > 0 && (
            <div className="mt-3 grid grid-cols-3 gap-2" data-testid="photo-thumbnails">
              {photos.map((p) => (
                <div
                  key={p.file_id}
                  className="relative group aspect-square border-2 border-black dark:border-white bg-neutral-100 dark:bg-neutral-900"
                  data-testid={`photo-thumb-${p.file_id}`}
                >
                  <img
                    src={p.url}
                    alt={p.name}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(p.file_id)}
                    className="absolute top-1 right-1 w-7 h-7 bg-red-600 text-white border-2 border-black font-black flex items-center justify-center hover:bg-red-700"
                    data-testid={`photo-remove-${p.file_id}`}
                    aria-label="Remove photo"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Advanced Options Toggle */}
        <button
          type="button"
          onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          className="w-full flex items-center justify-between p-3 bg-neutral-100 dark:bg-neutral-800 border-2 border-dashed border-neutral-400 dark:border-neutral-600 hover:border-[#2B4C6F] transition-colors"
          data-testid="advanced-options-toggle"
        >
          <span className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400 uppercase tracking-wider text-xs font-bold">
            <Toolbox size={16} weight="bold" />
            Advanced Options
            {(customLaborRate || contingencyPercent !== 10) && (
              <span className="px-2 py-0.5 bg-[#2B4C6F] text-white text-[10px] rounded-full">
                CUSTOM
              </span>
            )}
          </span>
          <CaretDown size={16} className={`text-neutral-500 transition-transform ${showAdvancedOptions ? 'rotate-180' : ''}`} />
        </button>

        {/* Advanced Options Panel */}
        {showAdvancedOptions && (
          <div className="p-4 bg-neutral-50 dark:bg-neutral-900 border-2 border-black dark:border-white space-y-5" data-testid="advanced-options-panel">
            {/* Labor Rate Customization */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="font-bold text-neutral-900 dark:text-white uppercase tracking-wider text-xs flex items-center gap-1">
                  <UsersThree size={14} weight="bold" />
                  LABOR RATE
                </Label>
                {customLaborRate && (
                  <button
                    type="button"
                    onClick={() => setCustomLaborRate(null)}
                    className="text-xs text-neutral-500 hover:text-red-500 uppercase tracking-wider"
                  >
                    Reset
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 font-bold">$</span>
                  <input
                    type="number"
                    min={laborRateInfo.min}
                    max={laborRateInfo.max}
                    value={customLaborRate || laborRateInfo.default}
                    onChange={(e) => setCustomLaborRate(parseInt(e.target.value) || null)}
                    className="w-full h-10 pl-7 pr-12 border-2 border-black dark:border-white bg-white dark:bg-[#0F172A] rounded-none font-mono text-lg font-bold"
                    data-testid="labor-rate-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 text-sm">/hr</span>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2 text-xs text-neutral-500">
                <span>Range: ${laborRateInfo.min} - ${laborRateInfo.max}/hr</span>
                <span>Default: ${laborRateInfo.default}/hr</span>
              </div>
              {formData.job_type && (
                <p className="mt-1 text-xs text-[#2B4C6F]">
                  Rate for {formData.job_type.replace('_', ' ')} jobs in your area
                </p>
              )}
            </div>

            {/* Contingency Adjuster */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="font-bold text-neutral-900 dark:text-white uppercase tracking-wider text-xs flex items-center gap-1">
                  <ShieldCheck size={14} weight="bold" />
                  CONTINGENCY BUFFER
                </Label>
                <span className="font-mono font-bold text-[#2B4C6F]">{contingencyPercent}%</span>
              </div>
              <input
                type="range"
                min="5"
                max="25"
                value={contingencyPercent}
                onChange={(e) => setContingencyPercent(parseInt(e.target.value))}
                className="w-full h-3 bg-neutral-200 dark:bg-neutral-700 border-2 border-black dark:border-white appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-[#2B4C6F] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-black [&::-webkit-slider-thumb]:cursor-pointer"
                data-testid="contingency-slider"
              />
              <div className="flex justify-between mt-1 text-xs text-neutral-500 font-mono">
                <span>5%</span>
                <span>10%</span>
                <span>15%</span>
                <span>20%</span>
                <span>25%</span>
              </div>
              <p className="mt-2 text-xs text-neutral-500">
                {contingencyPercent < 10 && "⚠️ Low buffer - suitable for simple, predictable jobs"}
                {contingencyPercent >= 10 && contingencyPercent <= 15 && "✓ Recommended for most residential projects"}
                {contingencyPercent > 15 && contingencyPercent <= 20 && "✓ Good for older homes or complex jobs"}
                {contingencyPercent > 20 && "⚡ High buffer - best for renovations with unknowns"}
              </p>
            </div>

            {/* Quick Presets */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-neutral-300 dark:border-neutral-700">
              <span className="text-xs text-neutral-500 uppercase tracking-wider w-full mb-1">Quick Presets:</span>
              <button
                type="button"
                onClick={() => { setContingencyPercent(5); setCustomLaborRate(null); }}
                className="px-3 py-1 text-xs font-bold border-2 border-black dark:border-white bg-white dark:bg-neutral-800 hover:bg-green-100 dark:hover:bg-green-900/30"
              >
                Budget
              </button>
              <button
                type="button"
                onClick={() => { setContingencyPercent(10); setCustomLaborRate(null); }}
                className="px-3 py-1 text-xs font-bold border-2 border-black dark:border-white bg-white dark:bg-neutral-800 hover:bg-blue-100 dark:hover:bg-blue-900/30"
              >
                Standard
              </button>
              <button
                type="button"
                onClick={() => { setContingencyPercent(15); setCustomLaborRate(Math.round(laborRateInfo.default * 1.1)); }}
                className="px-3 py-1 text-xs font-bold border-2 border-black dark:border-white bg-white dark:bg-neutral-800 hover:bg-amber-100 dark:hover:bg-amber-900/30"
              >
                Premium
              </button>
              <button
                type="button"
                onClick={() => { setContingencyPercent(20); setCustomLaborRate(Math.round(laborRateInfo.default * 1.2)); }}
                className="px-3 py-1 text-xs font-bold border-2 border-black dark:border-white bg-white dark:bg-neutral-800 hover:bg-purple-100 dark:hover:bg-purple-900/30"
              >
                High-End
              </button>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={loading}
          className="w-full h-14 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-black uppercase tracking-wider border-2 border-black dark:border-white shadow-[4px_4px_0px_0px_#000000] dark:shadow-[4px_4px_0px_0px_#2B4C6F] hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_0px_#000000] dark:hover:shadow-[6px_6px_0px_0px_#2B4C6F] active:translate-y-0 active:translate-x-0 active:shadow-[2px_2px_0px_0px_#000000] transition-all rounded-none disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="generate-estimate-btn"
        >
          <Sparkle size={20} weight="fill" className="mr-2" />
          {loading ? 'ANALYZING...' : 'GENERATE ESTIMATE'}
        </Button>
      </form>

      {/* History Toggle */}
      <div className="border-t-2 border-black dark:border-white">
        <button
          onClick={() => {
            setShowHistory(!showHistory);
            if (!showHistory && history.length === 0) fetchHistory();
          }}
          className="w-full flex items-center justify-between p-4 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors"
          data-testid="history-toggle"
        >
          <span className="flex items-center gap-2 uppercase tracking-wider text-xs font-bold">
            <ClockCounterClockwise size={16} weight="bold" />
            PAST ESTIMATES
          </span>
          <CaretDown size={16} className={`transition-transform ${showHistory ? 'rotate-180' : ''}`} />
        </button>

        {showHistory && (
          <div className="border-t-2 border-black dark:border-white p-4 bg-neutral-50 dark:bg-neutral-900 max-h-60 overflow-y-auto">
            {historyLoading ? (
              <p className="text-neutral-500 text-sm text-center py-4">Loading...</p>
            ) : history.length === 0 ? (
              <p className="text-neutral-500 text-sm text-center py-4">No past estimates</p>
            ) : (
              <div className="space-y-2">
                {history.map((est) => (
                  <div key={est.id} className="p-3 bg-white dark:bg-[#0F172A] border border-neutral-300 dark:border-neutral-700">
                    <p className="font-bold text-neutral-900 dark:text-white text-sm uppercase">{est.job_type}</p>
                    <p className="text-xs text-neutral-500">{est.location || 'No location'}</p>
                    <p className="font-mono text-[#2B4C6F] text-sm mt-1">{est.estimate_range}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const PHOTO_URL_BASE = PHOTO_BASE;
